import { useCallback, useContext, useState } from 'react';
import MoreHorizIcon from '@mui/icons-material//MoreHoriz';
import RefreshIcon from '@mui/icons-material//Refresh';
import { LinearProgress, MenuItem, Typography } from '@mui/material';
import { useFormat } from 'common/hooks';
import { FinancialStatementPeriod } from 'common/types/financials';
import { UseFormatValues } from 'common/types/format/format';
import { ValuationsApproach } from 'common/types/valuation/valuation';
import { useGetPublicCompListBatchByDate } from 'services/hooks';
import { dbShortDate, formatDate } from 'utilities';
import { RefreshCalibrationOptionProps, UseGetPublicCompListBatchByDateType } from './RefreshCalibrationOption.props';
import ValuationContext from '../../../ValuationsAllocation/ValuationContext';
import { useRefreshHelpersWithContext } from '../RefreshGPCOption/hooks';
import { refreshGPCOptionClasses } from '../RefreshGPCOption/RefreshGPCOption.styles';

const RefreshCalibrationOption = ({
  calibration,
  approaches,
  setApproaches,
  closeApproachMenu,
}: RefreshCalibrationOptionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { financialsPeriods, valuation } = useContext(ValuationContext);
  const [format] = useFormat() as UseFormatValues;
  const { getTickerListsByDate, getRefreshedApproaches } = useRefreshHelpersWithContext();
  const [, fetchGPCCompListsByDate]
    = useGetPublicCompListBatchByDate() as UseGetPublicCompListBatchByDateType;

  const refreshCalibrationApproach = useCallback(async () => {
    if (isLoading || !financialsPeriods) {
      return;
    }
    setIsLoading(true);
    try {
      const calibrationApproachId = calibration?.approach?.id ?? 0;
      if (calibrationApproachId !== 0) {
        const calibrationApproach
          = approaches?.find(appr => appr?.id === calibrationApproachId) ?? ([] as ValuationsApproach);
        const tickerListsByDate = getTickerListsByDate(
          [calibrationApproach],
          approaches,
          dbShortDate(calibration?.calibration_date)
        );
        // We await the fetch result so calibration changes are applied only from this user-triggered refresh.
        const publicCompsDataByDate = await fetchGPCCompListsByDate(
          tickerListsByDate,
          format.currency.code as string,
          financialsPeriods as FinancialStatementPeriod[]
        );

        if (publicCompsDataByDate) {
          const refreshedApproaches = getRefreshedApproaches({
            publicCompsDataByDate,
            outdatedApproaches: [calibrationApproach],
            approaches,
            marketDate: dbShortDate(calibration?.calibration_date),
          });

          // This is for not lost the user introduced and not saved data when
          // the useEffect on src/pages/Valuations/Valuation.js try to syncronize
          // the valuation.valuations_approaches with the "approaches" state variable.
          // Actually we do the same when we add/remove an approach
          valuation!.valuations_approaches = [...refreshedApproaches];

          // redraw approaches Accordion with the updated approach
          setApproaches([...refreshedApproaches]);
        }
      }
      closeApproachMenu();
    } finally {
      setIsLoading(false);
    }
  }, [
    approaches,
    calibration?.approach?.id,
    calibration?.calibration_date,
    fetchGPCCompListsByDate,
    financialsPeriods,
    format.currency.code,
    getTickerListsByDate,
    isLoading,
    closeApproachMenu,
    getRefreshedApproaches,
    valuation,
    setApproaches,
  ]);

  return (
    <div>
      {isLoading ? (
        <LinearProgress color="secondary" />
      ) : (
        <LinearProgress color="secondary" variant="determinate" value={100} />
      )}
      <MenuItem onClick={refreshCalibrationApproach}>
        <div className={refreshGPCOptionClasses.menuItemRow}>
          <div>
            <Typography color="secondary" variant="h4">
              {isLoading ? 'REFRESHING' : 'REFRESH DATA'}
            </Typography>
            <Typography className={refreshGPCOptionClasses.refreshDateLabel} color="secondary" variant="caption">
              LAST REFRESH DATE: {formatDate({ date: calibration?.last_refresh_date ?? new Date().toUTCString() })}
            </Typography>
          </div>
          <div className={refreshGPCOptionClasses.refreshIconContainer}>
            {isLoading ? (
              <MoreHorizIcon color="secondary" fontSize="small" />
            ) : (
              <RefreshIcon color="secondary" fontSize="small" />
            )}
          </div>
        </div>
      </MenuItem>
    </div>
  );
};

export default RefreshCalibrationOption;
